import styled, { css } from 'styled-components';

export const ModalSubtitle = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
  line-height: 1.45;
`;

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 20px 22px;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  background: #ffffff;

  & + & {
    margin-top: 14px;
  }
`;

export const SectionHead = styled.header`
  display: flex;
  align-items: center;
  gap: 10px;

  .badge {
    width: 32px;
    height: 32px;
    border-radius: 10px;
    background: #e6f3ec;
    color: #006633;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  h4 {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.01em;
  }

  .subtitle {
    display: block;
    margin-top: 2px;
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
  }
`;

export const Grid = styled.div<{ $cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $cols = 2 }) => $cols}, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

export const FieldBlock = styled.div<{ $span?: number }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  grid-column: span ${({ $span = 1 }) => $span};

  @media (max-width: 720px) {
    grid-column: span 1;
  }
`;

export const Label = styled.label`
  margin: 0;
  font-size: 11.5px;
  font-weight: 700;
  color: #475569;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const inputBase = css`
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13.5px;
  color: #0f172a;
  background: #ffffff;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    outline: none;
    border-color: #006633;
    box-shadow: 0 0 0 3px rgba(0, 102, 51, 0.12);
  }

  &:disabled {
    background: #f8fafc;
    color: #94a3b8;
    cursor: not-allowed;
  }
`;

export const TextInput = styled.input`
  ${inputBase};
`;

export const MaskedInputWrapper = styled.div`
  .form-control {
    ${inputBase};
  }
`;

export const Select = styled.select`
  ${inputBase};
  appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 34px;
  cursor: pointer;
`;

export const LogoUpload = styled.label<{ $hasImage: boolean }>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px;
  border: 1px dashed ${({ $hasImage }) => ($hasImage ? '#c7e4d5' : '#e2e8f0')};
  border-radius: 12px;
  background: ${({ $hasImage }) => ($hasImage ? '#f1faf4' : '#fafbfc')};
  cursor: pointer;
  transition: all 0.15s ease;
  margin: 0;

  &:hover {
    border-color: #006633;
    background: #f1faf4;
  }

  input {
    display: none;
  }

  .preview {
    width: 56px;
    height: 56px;
    border-radius: 10px;
    background: #ffffff;
    border: 1px solid #eef2f7;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;

    img {
      max-width: 100%;
      max-height: 100%;
    }

    svg {
      color: #94a3b8;
    }
  }

  .info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;

    .title {
      font-size: 13px;
      font-weight: 700;
      color: #0f172a;
      letter-spacing: -0.01em;
    }

    .desc {
      font-size: 11.5px;
      color: #64748b;
      font-weight: 500;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

export const FooterHint = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-top: 14px;
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 10px;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  line-height: 1.45;

  svg {
    flex-shrink: 0;
    margin-top: 1px;
    color: #94a3b8;
  }
`;

export const FooterButton = styled.button<{
  $variant?: 'primary' | 'secondary' | 'danger';
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 38px;
  padding: 0 18px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s ease;

  ${({ $variant = 'primary' }) => {
    if ($variant === 'primary') {
      return css`
        background: #006633;
        color: #ffffff;
        border: 1px solid #006633;

        &:hover:not(:disabled) {
          background: #00451f;
        }
      `;
    }
    if ($variant === 'danger') {
      return css`
        background: #dc2626;
        color: #ffffff;
        border: 1px solid #dc2626;

        &:hover:not(:disabled) {
          background: #b91c1c;
        }
      `;
    }
    return css`
      background: #ffffff;
      color: #0f172a;
      border: 1px solid #e2e8f0;

      &:hover:not(:disabled) {
        background: #f8fafc;
        border-color: #006633;
        color: #006633;
      }
    `;
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const FieldError = styled.span`
  font-size: 11.5px;
  color: #b91c1c;
  font-weight: 600;
`;
